import React from 'react';
import { memoizeFunction, mergeStyleSets } from '@fluentui/react';
import clsx from 'clsx';

import { IAppTextProps, Text } from './Text';

const getStyles = memoizeFunction((maxLines?: number) =>
  mergeStyleSets({
    root: { display: maxLines ? '-webkit-box' : 'block', margin: 0, padding: 0 },
  })
);

/**
 * Use for an `<h1>` element, a top level heading.
 * There should only be one Heading1 (e.g., one `<h1>`) per page.
 *
 * @param props
 * @returns {React.ReactElement} An instance of Heading1
 */
const Heading1: React.FunctionComponent<IAppTextProps> = (props) => {
  const { children, className, variant = 'Title1' as const, maxLines, announce = false, ...other } = props;
  return (
    <Text
      as="h1"
      maxLines={maxLines}
      variant={variant}
      aria-live={announce ? 'polite' : undefined}
      className={clsx(getStyles(maxLines).root, className)}
      {...other}
    >
      {children}
    </Text>
  );
};

/**
 * Use for `<h2>` element, a second-level heading.
 * There can be many Heading2 components on a page,
 * but they should always be after one Heading1 (`<h1>`) in page hierarchy.
 *
 * @param props
 * @returns {React.ReactElement} An instance of Heading2
 */
const Heading2: React.FunctionComponent<IAppTextProps> = (props) => {
  const { children, className, variant = 'Title2' as const, maxLines, ...other } = props;
  return (
    <Text as="h2" maxLines={maxLines} variant={variant} className={clsx(getStyles(maxLines).root, className)} {...other}>
      {children}
    </Text>
  );
};

/**
 * Use for `<h3>` element, a third-level heading.
 *
 * @param props
 * @returns {React.ReactElement} An instance of Heading3
 */
const Heading3: React.FunctionComponent<IAppTextProps> = (props) => {
  const { children, className, variant = 'Title3' as const, maxLines, ...other } = props;
  return (
    <Text as="h3" maxLines={maxLines} variant={variant} className={clsx(getStyles(maxLines).root, className)} {...other}>
      {children}
    </Text>
  );
};

/**
 * Use for `<h4>` element, a fourth-level heading.
 *
 * @param props
 * @returns {React.ReactElement} An instance of Heading4
 */
const Heading4: React.FunctionComponent<IAppTextProps> = (props) => {
  const { children, className, variant = 'Subtitle2' as const, maxLines, ...other } = props;
  return (
    <Text as="h4" maxLines={maxLines} variant={variant} className={clsx(getStyles(maxLines).root, className)} {...other}>
      {children}
    </Text>
  );
};

export { Heading1, Heading2, Heading3, Heading4 };
